import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Schedule,
  LocationOn,
  Star,
  ArrowBack,
  ArrowForward,
  Pets,
  CheckCircle,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import LocationPicker from '../components/LocationPicker';
import PaymentCheckout from '../components/PaymentCheckout';

const BookService = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPets, setSelectedPets] = useState([]);
  const [serviceType, setServiceType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [pets, setPets] = useState([]);
  const [availableHandlers, setAvailableHandlers] = useState([]);
  const [handlerSelectionMode, setHandlerSelectionMode] = useState('any'); // 'any' or 'find'
  const [searchQuery, setSearchQuery] = useState('');
  const [locationRadius, setLocationRadius] = useState(5);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [selectedHandlers, setSelectedHandlers] = useState([]);
  const [allHandlers, setAllHandlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  // Pickup location state
  const [pickupLocation, setPickupLocation] = useState({
    latitude: null,
    longitude: null,
    address: ''
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileHandler, setProfileHandler] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  // The real ServiceBooking created (server-priced) before the payment step in
  // "any handler" mode, so the charge links to it and uses the server's total.
  const [createdBooking, setCreatedBooking] = useState(null);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [bookingError, setBookingError] = useState('');

  const steps = ['Select Pet', 'Choose Service', 'Pick Date & Time', 'Choose Handler', 'Review & Pay'];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch user's pets, available handler profiles, and service types in parallel
      const [petsData, handlersData, serviceTypesData] = await Promise.all([
        apiService.getPets(),
        apiService.getHandlerProfiles(),
        apiService.getServiceTypes()
      ]);
      
      // Handle paginated responses - extract results array if it exists
      const pets = Array.isArray(petsData) ? petsData : (petsData?.results || []);
      const handlers = Array.isArray(handlersData) ? handlersData : (handlersData?.results || []);
      const serviceTypes = Array.isArray(serviceTypesData) ? serviceTypesData : (serviceTypesData?.results || []);
      
      setPets(pets);
      setAllHandlers(handlers);
      setAvailableHandlers(handlers.filter(handler => handler.is_available === true));
      setServiceTypes(serviceTypes);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching initial data:', err);
      // Ensure state remains as empty arrays even on error
      setPets([]);
      setAllHandlers([]);
      setAvailableHandlers([]);
      setServiceTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
    '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM',
  ];

  const handleNext = () => {
    // Skip handler selection step if in 'any' mode
    if (activeStep === 2 && handlerSelectionMode === 'any') {
      setActiveStep(4); // Jump directly to payment
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    // Leaving the payment step discards the draft booking so that if the user
    // changes anything and returns, a fresh, correctly-priced booking is made.
    if (activeStep === 4) {
      setCreatedBooking(null);
      setPaymentAmount(0);
      setBookingError('');
    }
    // Skip handler selection step when going back if in 'any' mode
    if (activeStep === 4 && handlerSelectionMode === 'any') {
      setActiveStep(2); // Jump back from Review & Pay to date/time step
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  // Create the booking server-side once the user reaches the payment step in
  // "any handler" mode. The server prices it authoritatively; we pay off its
  // returned total and link the charge to the real booking id — rather than
  // charging a browser-computed amount for a booking that doesn't exist.
  useEffect(() => {
    if (activeStep !== 4 || handlerSelectionMode !== 'any') return;
    if (createdBooking || creatingBooking) return;

    let cancelled = false;
    (async () => {
      try {
        setCreatingBooking(true);
        setBookingError('');

        const serviceData = {
          pet_ids: selectedPets.map((pet) => pet.id),
          service_type_id: serviceType,
          scheduled_date: selectedDate,
          scheduled_time: convertTo24HourFormat(selectedTime),
          pickup_address: pickupLocation.address || 'Address not provided',
          pickup_latitude: pickupLocation.latitude,
          pickup_longitude: pickupLocation.longitude,
          special_instructions: `Service for ${selectedPets.map((p) => p.name).join(', ')}`,
          any_handler: true,
        };

        const created = await apiService.createServiceBooking(serviceData);
        if (cancelled) return;

        setCreatedBooking(created);
        // total_price is in ZAR; PaymentCheckout works in cents.
        setPaymentAmount(Math.round(Number(created.total_price) * 100));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to create booking:', err);
        setBookingError(
          err.message || 'Could not prepare your booking. Please try again.',
        );
      } finally {
        if (!cancelled) setCreatingBooking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, handlerSelectionMode]);

  const togglePetSelection = (pet) => {
    setSelectedPets(prev => {
      const isSelected = prev.some(p => p.id === pet.id);
      if (isSelected) {
        return prev.filter(p => p.id !== pet.id);
      } else {
        return [...prev, pet];
      }
    });
  };

  const convertTo24HourFormat = (time12h) => {
    if (!time12h) return '';
    
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    
    hours = parseInt(hours, 10);
    
    if (modifier === 'AM') {
      if (hours === 12) {
        hours = 0; // 12 AM = 00:xx
      }
    } else if (modifier === 'PM') {
      if (hours !== 12) {
        hours += 12; // 1 PM = 13:xx, but 12 PM stays 12:xx
      }
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  const getFilteredHandlers = () => {
    const handlers = handlerSelectionMode === 'available' ? availableHandlers : allHandlers;
    
    return handlers.filter(handler => {
      // Filter by service type if one is selected
      const matchesServiceType = !serviceType || 
        handler.services?.some(service => service.service_type?.id === parseInt(serviceType)) ||
        handler.handler_services?.some(service => service.service_type?.id === parseInt(serviceType));
      
      const matchesSearch = !searchQuery || 
        handler.user?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        handler.user?.last_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRating = ratingFilter === 0 || handler.average_rating >= ratingFilter;
      
      return matchesServiceType && matchesSearch && matchesRating;
    });
  };

  const handleHandlerToggle = (handler) => {
    setSelectedHandlers(prev => {
      const isSelected = prev.some(h => h.id === handler.id);
      if (isSelected) {
        return prev.filter(h => h.id !== handler.id);
      } else {
        return [...prev, handler];
      }
    });
  };

  const handleSendToHandlers = async () => {
    try {
      setError('');
      setBooking(true);

      const serviceData = {
        pet_ids: selectedPets.map(pet => pet.id),
        service_type_id: serviceType,
        scheduled_date: selectedDate,
        scheduled_time: convertTo24HourFormat(selectedTime),
        pickup_address: pickupLocation.address || 'Address not provided',
        pickup_latitude: pickupLocation.latitude,
        pickup_longitude: pickupLocation.longitude,
        special_instructions: `Service for ${selectedPets.map(p => p.name).join(', ')}`,
        any_handler: false, // Specific handlers selected
      };

      const service = await apiService.createServiceBooking(serviceData);
      
      // Send requests to selected handlers
      const serviceRequestPromises = selectedHandlers.map(handler =>
        apiService.createServiceRequest({
          service: service.id,
          handler: handler.user?.id,
          message: `Request for service on ${selectedDate} at ${selectedTime}`
        })
      );
      await Promise.all(serviceRequestPromises);
      
      navigate('/dashboard', { 
        state: { message: `Service request sent to ${selectedHandlers.length} handler${selectedHandlers.length > 1 ? 's' : ''}!` }
      });
    } catch (err) {
      console.error('Error sending service requests:', err);
      // api.js throws a plain Error with `.message` (and `.data`/`.status`);
      // there is no axios-style `err.response`.
      setError(err.message || 'Failed to send service requests. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  // Helper function to group service types by category
  const getServiceTypesByCategory = () => {
    const grouped = {};
    serviceTypes.forEach(type => {
      const categoryName = type.category_name;
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(type);
    });
    return grouped;
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedPets.length > 0;
      case 1:
        return serviceType !== '';
      case 2:
        return selectedDate !== '' && selectedTime !== '';
      case 3:
        if (handlerSelectionMode === 'find') {
          return selectedHandlers.length > 0;
        }
        // This step is skipped for 'any' mode
        return false;
      case 4:
        // Review & Pay step - always allow
        return true;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Which pets need a service?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can select multiple pets for the same service.
              </Typography>
              {selectedPets.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected pets ({selectedPets.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedPets.map((pet) => (
                      <Chip
                        key={pet.id}
                        label={pet.name}
                        onDelete={() => togglePetSelection(pet)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
            {Array.isArray(pets) && pets.length > 0 ? pets.map((pet) => {
              const isSelected = selectedPets.some(p => p.id === pet.id);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pet.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      '&:hover': { borderColor: theme.palette.primary.light },
                      bgcolor: isSelected ? theme.palette.primary.light + '10' : 'background.paper',
                    }}
                    onClick={() => togglePetSelection(pet)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, fontSize: '2rem' }}>
                        {pet.avatar}
                      </Avatar>
                      <Typography variant="h6">{pet.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pet.breed}
                      </Typography>
                      {isSelected && (
                        <Chip
                          label="Selected"
                          color="primary"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            }) : (
              <Grid size={12}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No pets available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add a pet to your profile to book services.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/pets')}
                    sx={{ mt: 2, textTransform: 'none' }}
                  >
                    Add Pet
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              What type of service?
            </Typography>
            
            {(() => {
              const groupedServices = getServiceTypesByCategory();
              const categories = Object.keys(groupedServices);
              
              return (
                <Box>
                  {/* Category Filter */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      Filter by category:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        label="All Categories"
                        onClick={() => setSelectedCategory('')}
                        color={selectedCategory === '' ? 'primary' : 'default'}
                        variant={selectedCategory === '' ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                      {categories.map((categoryName) => (
                        <Chip
                          key={categoryName}
                          label={categoryName}
                          onClick={() => setSelectedCategory(categoryName)}
                          color={selectedCategory === categoryName ? 'primary' : 'default'}
                          variant={selectedCategory === categoryName ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Service Types by Category */}
                  {categories
                    .filter(categoryName => !selectedCategory || categoryName === selectedCategory)
                    .map((categoryName) => (
                    <Box key={categoryName} sx={{ mb: 4 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          borderBottom: `2px solid ${theme.palette.primary.light}`,
                          pb: 1
                        }}
                      >
                        {categoryName} ({groupedServices[categoryName].length} services)
                      </Typography>
                      <Grid container spacing={3}>
                        {groupedServices[categoryName].map((type) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type.id}>
                            <Card
                              sx={{
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                border: serviceType === type.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                // Custom per-service background image sits behind the content.
                                ...(type.background_image && {
                                  backgroundImage: `url(${type.background_image})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                }),
                                '&:hover': {
                                  borderColor: theme.palette.primary.light,
                                  transform: 'translateY(-2px)',
                                  transition: 'all 0.2s ease-in-out'
                                },
                                transition: 'all 0.2s ease-in-out',
                                height: '100%'
                              }}
                              onClick={() => setServiceType(type.id)}
                            >
                              {/* Legibility scrim over the background image */}
                              {type.background_image && (
                                <Box sx={{
                                  position: 'absolute', inset: 0,
                                  bgcolor: 'rgba(255,255,255,0.82)',
                                }} />
                              )}
                              <CardContent sx={{ position: 'relative', textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box>
                                  {type.icon_image ? (
                                    <Box
                                      component="img"
                                      src={type.icon_image}
                                      alt={type.name}
                                      sx={{ width: 48, height: 48, objectFit: 'contain', mb: 2 }}
                                    />
                                  ) : (
                                    <Schedule sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
                                  )}
                                  <Typography variant="h6" sx={{ mb: 1 }}>{type.name}</Typography>
                                  {type.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {type.description}
                                    </Typography>
                                  )}
                                </Box>
                                {serviceType === type.id && (
                                  <Box sx={{ mt: 2 }}>
                                    <Chip 
                                      label="Selected" 
                                      color="primary" 
                                      size="small"
                                      icon={<CheckCircle />}
                                    />
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              When would you like the service?
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Time</InputLabel>
                  <Select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    label="Time"
                  >
                    {timeSlots.map((time) => (
                      <MenuItem key={time} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Pickup Location */}
            <Box sx={{ mt: 4 }}>
              <LocationPicker
                initialLatitude={pickupLocation.latitude}
                initialLongitude={pickupLocation.longitude}
                initialAddress={pickupLocation.address}
                onLocationChange={(locationData) => {
                  setPickupLocation({
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    address: locationData.address || ''
                  });
                }}
                label="Pickup Location for Service"
                required={true}
                useDefaultAddress={true}
              />
            </Box>

            {/* Handler Preference Selection */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Handler Preference
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: handlerSelectionMode === 'any' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      '&:hover': { borderColor: theme.palette.primary.light },
                      bgcolor: handlerSelectionMode === 'any' ? 'primary.50' : 'background.paper',
                    }}
                    onClick={() => setHandlerSelectionMode('any')}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Any Available Handler
                      </Typography>
                      <Chip label="Recommended" color="primary" size="small" sx={{ mt: 1, mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Fastest response time. Any verified handler in your area can accept your request.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: handlerSelectionMode === 'find' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      '&:hover': { borderColor: theme.palette.primary.light },
                      bgcolor: handlerSelectionMode === 'find' ? 'primary.50' : 'background.paper',
                      opacity: getFilteredHandlers().length === 0 ? 0.6 : 1,
                    }}
                    onClick={() => {
                      if (getFilteredHandlers().length > 0) {
                        setHandlerSelectionMode('find');
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Choose Specific Handler
                      </Typography>
                      {getFilteredHandlers().length === 0 ? (
                        <Chip label="No handlers available" color="warning" size="small" sx={{ mt: 1, mb: 2 }} />
                      ) : (
                        <>
                          {/* Spacer for alignment */}
                          <Box sx={{ height: '28px', mb: 2 }} />
                        </>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {getFilteredHandlers().length === 0 
                          ? 'No handlers currently offer this service. Use "Any Available Handler" instead.'
                          : 'Browse and select from available handlers in your area.'
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 3:
        // This step only appears when handlerSelectionMode is 'find'
        const filteredHandlers = getFilteredHandlers();
        
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Choose your handler
            </Typography>
            
            {/* Search and Filters */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Search & Filters
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Search by name or specialty"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g., Sarah, Large breeds"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Location Radius</InputLabel>
                    <Select
                      value={locationRadius}
                      onChange={(e) => setLocationRadius(e.target.value)}
                      label="Location Radius"
                    >
                      <MenuItem value={2}>2 km</MenuItem>
                      <MenuItem value={5}>5 km</MenuItem>
                      <MenuItem value={8}>8 km</MenuItem>
                      <MenuItem value={15}>15 km</MenuItem>
                      <MenuItem value={40}>40 km</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Min Rating</InputLabel>
                    <Select
                      value={ratingFilter}
                      onChange={(e) => setRatingFilter(e.target.value)}
                      label="Min Rating"
                    >
                      <MenuItem value={0}>Any Rating</MenuItem>
                      <MenuItem value={3.0}>3.0+ Stars</MenuItem>
                      <MenuItem value={4.0}>4.0+ Stars</MenuItem>
                      <MenuItem value={4.5}>4.5+ Stars</MenuItem>
                      <MenuItem value={4.8}>4.8+ Stars</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Results Count */}
            {filteredHandlers.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Found {filteredHandlers.length} handler{filteredHandlers.length > 1 ? 's' : ''} within {locationRadius} km
              </Typography>
            )}

            {/* Handlers Grid */}
            <Grid container spacing={3}>
              {filteredHandlers.map((handler) => {
                const isSelected = selectedHandlers.find(h => h.id === handler.id);
                
                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={handler.id || `handler-${Math.random()}`}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        '&:hover': { borderColor: theme.palette.primary.light },
                        opacity: handler.is_available === false ? 0.7 : 1,
                      }}
                      onClick={() => handleHandlerToggle(handler)}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                              {(handler.user?.first_name || 'H')[0]}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">
                                  {handler.user?.full_name || `${handler.user?.first_name || ''} ${handler.user?.last_name || ''}`.trim()}
                                </Typography>
                                {!handler.is_available && (
                                  <Chip label="Busy" size="small" color="warning" />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Star sx={{ fontSize: 16, color: 'gold' }} />
                                <Typography variant="body2">
                                  {parseFloat(handler.average_rating || 0).toFixed(1)} ({handler.total_reviews || 0} reviews)
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Tooltip title="View Profile">
                            <IconButton onClick={e => { e.stopPropagation(); handleOpenProfileModal(handler); }} size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {handler.bio || 'Professional pet handler'}
                        </Typography>
                        
                        {/* Services Offered */}
                        {handler.handler_services && handler.handler_services.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Services offered:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {handler.handler_services.slice(0, 3).map((service, index) => (
                                <Chip
                                  key={service.id || index}
                                  label={`${service.service_type?.name || 'Service'} - R${parseFloat(service.base_price || 0).toFixed(0)}`}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                              {handler.handler_services.length > 3 && (
                                <Chip
                                  label={`+${handler.handler_services.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                        
                        <Typography variant="body2" color="text.secondary">
                          <LocationOn sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          {handler.distance || 'Distance unknown'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* No Results */}
            {filteredHandlers.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  {serviceType ? 'No handlers available for this service' : 'No handlers found'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {serviceType 
                    ? 'No handlers currently offer this service in your area. You can still book by selecting "Any Available Handler" which will open your request to all verified handlers.'
                    : 'Try adjusting your search filters or expanding the location radius'
                  }
                </Typography>
                {serviceType && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setHandlerSelectionMode('any');
                      setActiveStep(4); // Jump to payment
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Use Any Available Handler
                  </Button>
                )}
              </Box>
            )}

            {/* Selected Handlers Summary */}
            {selectedHandlers.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedHandlers.length} handler{selectedHandlers.length > 1 ? 's' : ''} selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedHandlers.map(handler => (
                    <Chip
                      key={handler.user?.id}
                      label={handler.user?.full_name || `${handler.user?.first_name || ''} ${handler.user?.last_name || ''}`.trim()}
                      onDelete={() => handleHandlerToggle(handler)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 4:
        const selectedServiceType = serviceTypes.find(type => type.id === serviceType);
        const serviceTypeForReview = selectedServiceType;

        // The booking is being (or failed to be) created server-side. Don't
        // show the payment UI until we have a real, priced booking.
        if (creatingBooking) {
          return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
                Preparing your booking...
              </Typography>
            </Box>
          );
        }

        if (bookingError) {
          return (
            <Box sx={{ py: 4 }}>
              <Alert severity="error" sx={{ mb: 2 }}>{bookingError}</Alert>
              <Button
                variant="contained"
                onClick={() => { setCreatedBooking(null); setBookingError(''); }}
              >
                Try Again
              </Button>
            </Box>
          );
        }

        if (!createdBooking) {
          return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          );
        }

        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review Booking & Complete Payment
            </Typography>

            {/* Booking Summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Booking Summary
              </Typography>
              <List>
                {/* Show all selected pets */}
                {selectedPets.map((pet, index) => (
                  <React.Fragment key={pet.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>{pet.avatar}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={pet.name}
                        secondary={pet.breed}
                      />
                    </ListItem>
                    {index < selectedPets.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
                <Divider />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <Schedule />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={serviceTypeForReview?.name}
                    secondary={`${selectedDate} at ${selectedTime}`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                      <LocationOn />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Pickup Location"
                    secondary={pickupLocation.address || 'Address not provided'}
                  />
                </ListItem>
                <Divider />
                
                {/* Handler Selection Display */}
                {handlerSelectionMode === 'find' ? (
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                        {selectedHandlers.length}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`Sending to ${selectedHandlers.length} handler${selectedHandlers.length > 1 ? 's' : ''}`}
                      secondary={selectedHandlers.map(h => h.user?.full_name || `${h.user?.first_name || ''} ${h.user?.last_name || ''}`.trim()).join(', ')}
                    />
                  </ListItem>
                ) : (
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                        <Pets />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Any Available Handler"
                      secondary="Request open to any verified handler"
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            {/* Payment Section */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You'll be redirected to Stitch's secure payment page to complete your R{(paymentAmount / 100).toFixed(2)} payment.
            </Typography>
            
            <PaymentCheckout
              amount={paymentAmount}
              bookingDetails={{
                id: createdBooking.id,
                description: `${selectedServiceType?.name || 'Pet service'} booking`,
                serviceType: selectedServiceType?.name,
                petName: selectedPets.map(p => p.name).join(', '),
                date: `${selectedDate} at ${selectedTime}`,
              }}
              onPaymentSuccess={(result) => {
                // Payment successful - navigate to dashboard
                navigate('/dashboard', {
                  state: { message: 'Booking completed successfully!' }
                });
              }}
              onPaymentCancel={() => {
                // User cancelled payment - stay on this step
                setError('Payment was cancelled. Please try again.');
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const handleOpenProfileModal = (handler) => {
    setProfileHandler(handler);
    setProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setProfileModalOpen(false);
    setProfileHandler(null);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        Book a Service
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        Schedule a service for your pets
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, mb: 4 }}>
        {renderStepContent()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={activeStep === 0}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        
        {activeStep === 4 ? (
          // Payment step - PaymentCheckout component handles its own button
          null
        ) : activeStep === 3 && handlerSelectionMode === 'find' && selectedHandlers.length > 0 ? (
          <Button
            variant="contained"
            onClick={handleSendToHandlers}
            disabled={booking}
            sx={{ textTransform: 'none' }}
          >
            {booking ? 'Sending...' : 'Send Request to Handlers'}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            disabled={!canProceed()}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        )}
      </Box>

      <Dialog open={profileModalOpen} onClose={handleCloseProfileModal} maxWidth="xs" fullWidth>
        <DialogTitle>Handler Profile</DialogTitle>
        <DialogContent>
          {profileHandler && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, fontSize: '2rem' }}>
                {profileHandler.user?.first_name?.[0] || 'H'}
              </Avatar>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {profileHandler.user?.full_name || `${profileHandler.user?.first_name || ''} ${profileHandler.user?.last_name || ''}`.trim()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {profileHandler.bio || 'Professional pet handler'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Star sx={{ fontSize: 18, color: 'gold' }} />
                <Typography variant="body2">
                  {parseFloat(profileHandler.average_rating || 0).toFixed(1)} ({profileHandler.total_reviews || 0} reviews)
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Services Offered
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 2 }}>
                {profileHandler.handler_services && profileHandler.handler_services.length > 0 ? (
                  profileHandler.handler_services.map((service, idx) => (
                    <Chip
                      key={service.id || idx}
                      label={`${service.service_type?.name || 'Service'} - R${parseFloat(service.base_price || 0).toFixed(0)}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No services listed</Typography>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Service radius: {profileHandler.service_radius_miles || 0} miles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hourly rate: R{parseFloat(profileHandler.hourly_rate || 0).toFixed(0)}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default BookService;